import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PrismaPromptDto, PromptsService, UpdateTemplateBodyDto } from 'app/API';
import { llmPromptsKeys } from 'app/queries/llmPrompts/llmPromptsKeys';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { createFetchOpenAiModelsQueryFn } from '../../api/fetchOpenAiModels';
import { createFetchPromptQueryFn } from '../../api/fetchPrompt';
import { createFetchPromptsQueryFn } from '../../api/fetchPrompts';
import { EditPrompt } from './components/EditPromptStage';
import { PreviewPrompt } from './components/SecondStage';
import { Wrapper } from './styles/Wrapper';
import { Select } from '@components/core-ui/form-elements/Select';

export const Content = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [stage, setStage] = useState('edit');
  const [chosenPromptSlug, setChosenPromptSlug] = useState<UpdateTemplateBodyDto['slug'] | undefined>(undefined);
  const [chosenPrompt, setChosenPrompt] = useState<PrismaPromptDto>({
    template: '',
    version: 0,
    variables: '',
    systemPrompt: '',
    defaultModel: '',
    id: '',
    slug: '',
    agentic: false,
    createdAt: '',
    updatedAt: '',
  });
  const [updatedSystemPrompt, setUpdatedSystemPrompt] = useState('');
  const [updatedTemplate, setUpdatedTemplate] = useState('');

  const promptsQuery = useQuery({
    queryKey: llmPromptsKeys.prompts(),
    queryFn: createFetchPromptsQueryFn(call),
  });
  const modelsQuery = useQuery({
    queryKey: llmPromptsKeys.openAiModels(),
    queryFn: createFetchOpenAiModelsQueryFn(call),
  });
  const promptQuery = useQuery({
    queryKey: llmPromptsKeys.promptDetail(chosenPromptSlug ?? ''),
    queryFn: createFetchPromptQueryFn(call),
    enabled: !!chosenPromptSlug,
  });

  const promptsSlugs = useMemo(
    () =>
      (promptsQuery.data ?? [])
        .map((prompt) => prompt?.slug)
        .filter((slug): slug is UpdateTemplateBodyDto['slug'] => Boolean(slug))
        .sort((a, b) => (a ?? '').localeCompare(b ?? '')),
    [promptsQuery.data],
  );
  const availableModels = modelsQuery.data ?? [];

  useEffect(() => {
    if (!promptsSlugs.length) {
      return;
    }
    setChosenPromptSlug((prev) => (prev && promptsSlugs.includes(prev) ? prev : promptsSlugs[0]));
  }, [promptsSlugs]);

  useEffect(() => {
    const prompt = promptQuery.data;
    if (!prompt) {
      return;
    }
    setChosenPrompt(prompt);
    setUpdatedTemplate(prompt.template);
    setUpdatedSystemPrompt(prompt.systemPrompt);
    setStage('edit');
  }, [promptQuery.data]);

  const manualReload = () => {
    setStage('edit');
    void queryClient.invalidateQueries({ queryKey: llmPromptsKeys.all });
  };

  const updateDefaultModelMutation = useMutation({
    mutationFn: ({ slug, model }: { slug: UpdateTemplateBodyDto['slug']; model: string }) =>
      call(() => PromptsService.updatePromptDefaultModel({ requestBody: { model }, slug }), {
        onError: (error) => toast.error(error),
      }),
    onSuccess: () => {
      toast.success('Default model updated successfully');
      void queryClient.invalidateQueries({ queryKey: llmPromptsKeys.promptDetails() });
    },
  });
  const isLoading = updateDefaultModelMutation.isPending;

  return (
    <Wrapper>
      <div className="layout">
        <aside className="sidebar">
          <div className="sidebar-header">Prompts</div>
          <div className="sidebar-list">
            {promptsSlugs.length ? (
              promptsSlugs.map((slug) => (
                <button
                  type="button"
                  key={slug}
                  onClick={() => setChosenPromptSlug(slug)}
                  className={`prompt-item ${slug === chosenPromptSlug ? 'active' : ''}`}
                >
                  {slug}
                </button>
              ))
            ) : (
              <div className="empty-state">No prompts available</div>
            )}
          </div>
        </aside>

        <div className="main-panel">
          {!!chosenPrompt?.version && (
            <div className="mb-2 ml-2 p-2 version">Version :{chosenPrompt.version.toFixed(2)}</div>
          )}

          {!!chosenPrompt?.variables.length && <div className="mb-2 ml-2 p-2 variables">Required variables:</div>}

          {!!chosenPrompt?.variables.length && (
            <div className="flex flex-wrap">
              {chosenPrompt?.variables?.split('|').map((variable) => {
                return (
                  <div className="mb-4 ml-2 p-2 variables flex flex-row border rounded-xl bg-gray-100" key={variable}>
                    {variable}
                  </div>
                );
              })}
            </div>
          )}

          {!!chosenPrompt?.defaultModel && <div className="mb-2 ml-2 p-2 variables">Prompt default model :</div>}
          {!!chosenPrompt?.defaultModel && (
            <div className="flex">
              {chosenPrompt?.defaultModel && (
                <div className="mb-4 w-full flex">
                  <div className="w-full max-w-[500px]">
                    <Select
                      options={availableModels.map((model) => ({ label: model, value: model }))}
                      label={'Available models'}
                      onChange={(_name, value) => {
                        setChosenPrompt((prev) => ({ ...prev, defaultModel: value }));
                      }}
                      value={chosenPrompt.defaultModel}
                    />
                  </div>
                  <div className="w-[200px] ml-4">
                    {!isLoading ? (
                      <Button
                        onClick={() => {
                          if (!chosenPromptSlug) {
                            return;
                          }
                          updateDefaultModelMutation.mutate({
                            slug: chosenPromptSlug,
                            model: chosenPrompt.defaultModel,
                          });
                        }}
                      >
                        Test and save
                      </Button>
                    ) : (
                      <AbsoluteSpinner overlay={true} />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {stage === 'edit' ? (
            <EditPrompt
              systemPrompt={chosenPrompt.systemPrompt}
              updatedSystemPrompt={updatedSystemPrompt}
              setSystemPrompt={setUpdatedSystemPrompt}
              template={chosenPrompt.template}
              updatedTemplate={updatedTemplate}
              setTemplate={setUpdatedTemplate}
              setStage={setStage}
            />
          ) : null}

          {stage === 'preview' && chosenPromptSlug ? (
            <PreviewPrompt
              setStage={setStage}
              left={chosenPrompt.template}
              right={updatedTemplate}
              slug={chosenPromptSlug}
              systemPrompt={updatedSystemPrompt}
              manualReload={manualReload}
            />
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
};
